import { AuthenticatedImage } from '@components/AuthenticatedImage/AuthenticatedImage';
import { AudioSelectedIcon } from '@components/Icons/AudioSelectedIcon';
import { VideoSelectedIcon } from '@components/Icons/VideoSelectedIcon';
import { PlaceholderGradient } from '@components/util/PlaceholderGradient';
import { getMediaThumbnail, useMediaThumbnail } from '@lib/image/thumbnail';
import { generateIdFromString } from '@lib/utils/colorUtils';
import { isAudioMedia } from '@lib/utils/fileExtensions';
import { FileUploadStatus } from '@stores/UploadManager/UploadManager';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import React, { useState } from 'react';
import css from './MediaThumbnail.module.scss';

interface MediaThumbnailProps {
  media: Media & { uploadStatus?: FileUploadStatus; uniqueId?: string };
  /** Additional className for the outer container */
  className?: string;
  /** CSS module overrides for inner elements */
  classNames?: {
    image?: string;
    blur?: string;
    lqipOverlay?: string;
    audioIcon?: string;
    videoIcon?: string;
  };
  /** Called when the image fails to load */
  onError?: (mediaId: string) => void;
  /** Whether a parent-tracked error exists for this media */
  hasExternalError?: boolean;
}

/**
 * Shared thumbnail renderer for media items. Handles:
 * - CDN thumbnails (authenticated and unauthenticated)
 * - Local upload thumbnails (LQIP / data URLs)
 * - Placeholder gradients with audio/video icons on error
 */
export const MediaThumbnail = React.memo<MediaThumbnailProps>(
  ({ media, className, classNames: cx, onError, hasExternalError }) => {
    const { url: backendUrl, token: backendToken } = useMediaThumbnail(media.libraryId, media._id);
    const [hasError, setHasError] = useState(false);

    const { url: cdnThumbnailUrl, token } = getMediaThumbnail(media, backendUrl, backendToken);
    const localThumbnail = media.uploadStatus?.localThumbnail;
    const thumbnailUrl = cdnThumbnailUrl || localThumbnail || null;
    const isLqip = thumbnailUrl?.startsWith('data:image/');

    const handleImageError = () => {
      setHasError(true);
      onError?.(media._id);
    };

    if (hasExternalError || hasError || !thumbnailUrl) {
      const gradientSeed = media.uniqueId || media.uploadStatus?.fingerprint || media._id;
      return (
        <div className={classNames(css.container, className)}>
          {localThumbnail && (
            <img
              src={localThumbnail}
              alt={media.metadata?.filename || 'Media'}
              className={classNames(
                css.image,
                cx?.image,
                css.blur,
                cx?.blur,
                css.lqipOverlay,
                cx?.lqipOverlay,
              )}
            />
          )}
          {gradientSeed && (
            <PlaceholderGradient id={generateIdFromString(gradientSeed)} blur={50} />
          )}
          {isAudioMedia(media) ? (
            <AudioSelectedIcon className={classNames(css.mediaTypeIcon, cx?.audioIcon)} />
          ) : (
            <VideoSelectedIcon className={classNames(css.mediaTypeIcon, cx?.videoIcon)} />
          )}
        </div>
      );
    }

    if (!token) {
      return (
        <div className={classNames(css.container, className)}>
          <img
            src={thumbnailUrl}
            alt={media.metadata?.filename || 'Media'}
            className={classNames(css.image, cx?.image, {
              [css.blur]: isLqip,
              [cx?.blur ?? '']: isLqip,
            })}
            onError={handleImageError}
          />
        </div>
      );
    }

    return (
      <div className={classNames(css.container, className)}>
        <AuthenticatedImage
          src={thumbnailUrl}
          alt={media.metadata?.filename || 'Media'}
          className={classNames(css.image, cx?.image)}
          token={token}
          onError={handleImageError}
        />
      </div>
    );
  },
);

MediaThumbnail.displayName = 'MediaThumbnail';
